import React, { useEffect, useRef, useState } from "react";
import styles from "../DisLoginOTPVerification/DisLoginOtp.module.scss";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "@/lib/toast";

import {
  getInitialValues,
  getItem,
  isCookiePresent,
  saveToken,
  setItem,
} from "@/lib/utils";

import Button from "@/components/Button/Button";
import AuthService from "@/services/api/auth/auth";
import Cookies from "js-cookie";
import formRenderer from "@/lib/formRenderer";
import { useForm } from "react-hook-form";
import DisRoutes from "@/routes/DisRoutes";
import { useRole } from "@/pages/Role/Role";

const otpLength = 4;
const TIMER_DURATION = 30;
const STORAGE_KEY = "otpTimerExpiry";

interface LocationState {
  email?: string;
}

const DisLoginOtpVerification: React.FC = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || getItem("otpEmail");
  const refArray: any = useRef<HTMLInputElement[]>([]);
  const { role: contextRole } = useRole();
  const { role: paramRole } = useParams<{ role: string }>();
  const role = contextRole || paramRole;
  const [timer, setTimer] = useState(0);

  const [expiryTime, setExpiryTime] = useState<number | null>(null);
  const [isResending, setIsResending] = useState(false);

  const formConfig = {
    otp: {
      controlType: "input",
      label: "Enter OTP",
      placeholder: "Enter 4-digit OTP",
      isRequired: true,
      validations: {
        required: "OTP is required",
        minLength: { value: 4, message: "OTP must be 4 digits" },
        maxLength: { value: 4, message: "OTP must be 4 digits" },
        pattern: {
          value: /^[0-9]{4}$/,
          message: "Only digits allowed",
        },
      },
      props: {
        maxLength: 4,
        inputMode: "numeric",
        onInput: (e: any) => {
          e.target.value = e.target.value.replace(/[^0-9]/g, "");
        },
        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
          const pasted = e.clipboardData.getData("text").trim();
          if (/^\d{4}$/.test(pasted)) {
            setValue("otp", pasted, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            });
            e.preventDefault();
          }
        },
      },
    },
  };

  const {
    register,
    handleSubmit: submitForm,
    control,
    setValue,
    formState,
  } = useForm({
    defaultValues: getInitialValues(formConfig),
    mode: "onChange",
  });

  const { isSubmitting, isValid } = formState;

  // focus first input
  useEffect(() => {
    if (refArray.current.length === 0) {
      refArray.current = Array(otpLength).fill(null);
    }

    setTimeout(() => {
      refArray.current[0]?.focus();
    }, 0);
  }, []);


  // initialize timer

  useEffect(() => {
      const savedExpiry: any = localStorage.getItem(STORAGE_KEY);
  
      if (savedExpiry) {
        const expiry = Number(savedExpiry);
        setExpiryTime(expiry);
        const remaining = Math.max(
          0,
          Math.ceil((expiry - Date.now()) / 1000)
        );
        setTimer(remaining); 
      } else {
        const newExpiry =
          Date.now() + TIMER_DURATION * 1000;
        localStorage.setItem(
          STORAGE_KEY,
          newExpiry.toString()
        );
        setExpiryTime(newExpiry);
        setTimer(TIMER_DURATION); 
      }
    }, []);


  // countdown logic

  useEffect(() => {

    if (!expiryTime) return;
    const interval = setInterval(() => {
      const remaining = Math.floor(
        (expiryTime - Date.now()) / 1000
      );

      if (remaining <= 0) {
        setTimer(0);
        setIsResending(false);
        clearInterval(interval);
        return;
      }
      setTimer(remaining);
    }, 1000);
    return () => clearInterval(interval);
  }, [expiryTime]);

  // verify OTP
  const onSubmit = async (values: any) => {
    try {
      const otpString = values.otp;
      if (otpString.length !== 4) {
        toast.error("Please enter 4-digit OTP");
        return;
      }

      const storedEmail = getItem("otpEmail");
      const email = location.state?.email || storedEmail;
      const res: any = await AuthService.DisValLoginOtp({
        emailOrMobileNo: email ?? "",
        otp: otpString,
      });

      if (res?.status === 200) {

        toast.success("Email verified successfully");
        localStorage.removeItem(STORAGE_KEY);

        if (!isCookiePresent("currentUser")) {

          var exDate = new Date();

          exDate.setDate(exDate.getDate() + 7);

          let currentUser = {

            userId: res.userId,

            accessToken: getItem("referraltoken"),

            userDetail: res.userDetail,

          };


          Cookies.set(

            "currentUser",

            JSON.stringify(currentUser),

            {

              expires: exDate,

              path: "/",

            }

          );

        }

        const token = res?.data.token;
        const username = res?.data.name;
        const distributorId = res?.data.id;
        const currency = res?.data?.currency;
        const retailerDistributorId = res.data.distributorId;
        const distributorName = res.data.distributorName;
        const emailRes = res?.data.email;

        if (role === "retailer") {
          setItem("disretailerId", retailerDistributorId);
          setItem("distributorName", distributorName);
        } else {
          setItem("distributorId", distributorId);
        }

        saveToken("referraltoken", token);
        setItem("email", emailRes);
        setItem("username", username);
        setItem("currency", currency);
        navigate(
          role === "distributor"
            ? `/${role}/${DisRoutes.privateRoutes?.Distributor?.path}`
            : `/${role}/${DisRoutes.privateRoutes?.RetailerPurchase?.path}`
        );
      } else if (res.status === 417) {
        toast.error(res.error);
      }
    } catch (err: any) {
      toast.error(err?.message);
    }
  };

  const handleResend = async (email: any) => {
    if (!email || timer > 0 || isResending) return;

    try {
      setIsResending(true);
      const res: any = await AuthService.DisGenerateLoginOtp(email);

      if (res.message === "Success") {
        toast.success(`OTP sent to ${email}`);
        const newExpiry = Date.now() + TIMER_DURATION * 1000;
        setItem(STORAGE_KEY, newExpiry);
        setExpiryTime(newExpiry);

      } else if (res.status === 417) {
        toast.error(res.error);
        setIsResending(false);
      }
    } catch (err: any) {
      toast.error(err.message);
      setIsResending(false);
    }
  };

  return (

    <div className={styles.OtpVermainDiv}>
      <p className={styles.headDiv}>
        Verify Email
      </p>

      <p className={styles.infoText}>
        One Time Password(OTP) has been sent
        <span className="text-blue-400">
          {" "}{email}
        </span>
      </p>

      <form
        className={styles.form}
        onSubmit={submitForm(onSubmit)}
      >

        {formRenderer(
          formConfig,
          formState,
          register,
          control
        )}

        <div className={styles.buttonsSection}>
          <Button
            type="submit"
            className={styles.verifyBtn}
            disabled={
              isSubmitting ||
              !isValid
            }
          >
            Verify Email
          </Button>

          <p
            className={`${styles.resendText}
            ${timer > 0 || isResending ? styles.disabled : ""}`}
            onClick={ timer > 0 || isResending ? undefined : () => handleResend(email)
            }
          >
            {timer > 0 ? `Resend code in ${timer}s` : "Resend OTP"}
          </p>
        </div>
      </form>
    </div>
  );

};

export default DisLoginOtpVerification;
